// packages/ui/src/contexts/useOlderReleaseRecovery.ts
'use client';
//
// Gets a page from another release (`@tourtask/utils/client/pageRelease`: the server did not know
// an action it called, because a deploy gave every action a new id) onto the live release. It
// reloads the page when that loses nothing and interrupts nobody (`olderReleaseRecovery`), so the
// next click works; otherwise `UIProvider` shows the "TourTask has been updated" notice, and the
// question is asked again each time the tab is hidden. UIProvider runs it, so every page that calls
// server actions (they need `useUI`) has it.

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { olderReleaseRecovery } from '@tourtask/utils/client/olderReleaseRecovery';
import { isPageFromOtherRelease, subscribePageRelease } from '@tourtask/utils/client/pageRelease';

import { hasUnsavedChanges } from '../forms/unsavedChangesRegistry';

/** When this tab last reloaded itself for this: per tab, and kept across the reload. */
const SELF_RELOAD_AT_KEY = 'tt:older-release-reload-at';

/** The time of this tab's last self-reload; a blocked storage counts as "just now", so it never loops. */
function lastSelfReloadAt(now: number): number | null {
  try {
    const stored = Number(sessionStorage.getItem(SELF_RELOAD_AT_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  } catch {
    return now;
  }
}

function stampSelfReload(now: number): boolean {
  try {
    sessionStorage.setItem(SELF_RELOAD_AT_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

const NON_TEXT_INPUT_TYPES = new Set([
  'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
]);

function isTextField(element: Element | null): boolean {
  if (element instanceof HTMLTextAreaElement) return true;
  if (element instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(element.type);
  return element instanceof HTMLElement && element.isContentEditable;
}

/**
 * A dialog on screen. Not merely one in the page: MUI keeps some closed ones mounted, hidden — the
 * backoffice's mobile menu is a drawer with `role="dialog"` on every page, which made every page
 * count as "a dialog is open" in the first walk.
 */
function isDialogOnScreen(): boolean {
  for (const dialog of document.querySelectorAll('[role="dialog"]')) {
    if (dialog.getClientRects().length > 0 && getComputedStyle(dialog).visibility !== 'hidden') return true;
  }
  return false;
}

/** Whether to show the notice; the reload itself happens here. */
export function useOlderReleaseRecovery(): { showNotice: boolean } {
  const fromOtherRelease = useSyncExternalStore(subscribePageRelease, isPageFromOtherRelease, () => false);
  const [showNotice, setShowNotice] = useState(false);
  const shownAtRef = useRef(0);
  const touchedSinceShownRef = useRef(false);
  // The page is being left: the browser hides every page it unloads, and that is no tab put aside.
  const leavingRef = useRef(false);

  // When the page was last shown, and whether anyone has clicked or typed since: tracked from the
  // start, because the page learns it is out of date only later (often right as its tab comes back).
  useEffect(() => {
    const shown = () => {
      shownAtRef.current = Date.now();
      touchedSinceShownRef.current = false;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') shown();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      leavingRef.current = false;
      if (event.persisted) shown();
    };
    const onPageHide = () => {
      leavingRef.current = true;
    };
    const touched = () => {
      touchedSinceShownRef.current = true;
    };
    shown();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pointerdown', touched, true);
    window.addEventListener('keydown', touched, true);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pointerdown', touched, true);
      window.removeEventListener('keydown', touched, true);
    };
  }, []);

  useEffect(() => {
    if (!fromOtherRelease) return;
    const recover = () => {
      const now = Date.now();
      const lastReload = lastSelfReloadAt(now);
      const recovery = olderReleaseRecovery({
        hidden: document.visibilityState === 'hidden',
        msSinceShown: now - shownAtRef.current,
        touchedSinceShown: touchedSinceShownRef.current,
        unsavedChanges: hasUnsavedChanges(),
        dialogOpen: isDialogOnScreen(),
        editingText: isTextField(document.activeElement),
        msSinceLastSelfReload: lastReload === null ? null : now - lastReload,
      });
      if (recovery === 'reload' && stampSelfReload(now)) {
        window.location.reload();
        return;
      }
      setShowNotice(true);
    };
    recover();
    const onVisibilityChange = () => {
      // `pagehide` comes first when the page is being left (a link, a closed tab): not a hidden tab.
      if (document.visibilityState === 'hidden' && !leavingRef.current) recover();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [fromOtherRelease]);

  return { showNotice };
}
